import React, { useRef } from 'react'
import { Platform } from 'react-native'
import Constants from 'expo-constants'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let NativeWebView: any = null
if (Platform.OS !== 'web') {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    NativeWebView = require('react-native-webview').WebView
  } catch {
    console.warn(
      '[reCAPTCHA] react-native-webview no disponible — se requiere rebuild del dev client',
    )
  }
}

// Clave pública de reCAPTCHA (domain-restricted en Google Console, no es un secreto)
const SITE_KEY: string =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (Constants.expoConfig?.extra as any)?.recaptchaSiteKey ||
  (process.env.EXPO_PUBLIC_RECAPTCHA_SITE_KEY as string) ||
  (process.env.RECAPTCHA_SITE_KEY as string) ||
  ''

declare global {
  interface Window {
    grecaptcha: {
      ready: (cb: () => void) => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
    }
  }
}

const RECAPTCHA_HTML = `<!DOCTYPE html>
<html>
<head>
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <script src="https://www.google.com/recaptcha/api.js?render=${SITE_KEY}"></script>
</head>
<body></body>
</html>`

export const useRecaptchaV3 = (action: string = 'login') => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const webViewRef = useRef<any>(null)
  const resolveRef = useRef<((token: string) => void) | null>(null)
  const rejectRef = useRef<((error: Error) => void) | null>(null)
  const scriptInjectedRef = useRef(false)

  const injectWebScript = () => {
    if (scriptInjectedRef.current) return
    scriptInjectedRef.current = true
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const doc = (global as any).document as any
    if (!doc || doc.getElementById('g-recaptcha-v3')) return
    const script = doc.createElement('script')
    script.id = 'g-recaptcha-v3'
    script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`
    doc.head.appendChild(script)
  }

  const getToken = (): Promise<string> => {
    if (!SITE_KEY) {
      console.warn(
        '[reCAPTCHA] Site key no configurado — EXPO_PUBLIC_RECAPTCHA_SITE_KEY está vacío',
      )
      return Promise.resolve('')
    }

    if (Platform.OS === 'web') {
      injectWebScript()
      return new Promise((resolve, reject) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const win = (global as any).window as any
        const deadline = Date.now() + 10_000
        const tryExecute = () => {
          if (Date.now() > deadline) {
            console.error(
              '[reCAPTCHA] Timeout esperando grecaptcha — script bloqueado o dominio no autorizado',
            )
            reject(new Error('reCAPTCHA load timeout'))
            return
          }
          if (!win?.grecaptcha) {
            setTimeout(tryExecute, 100)
            return
          }
          win.grecaptcha.ready(() => {
            win.grecaptcha
              .execute(SITE_KEY, { action })
              .then(resolve)
              .catch((err: Error) => {
                console.error('[reCAPTCHA] execute() falló:', err)
                reject(err)
              })
          })
        }
        tryExecute()
      })
    }

    // Android / iOS: ejecutar via WebView oculto
    if (!NativeWebView) {
      console.warn('[reCAPTCHA] WebView no disponible, token vacío')
      return Promise.resolve('')
    }

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        resolveRef.current = null
        rejectRef.current = null
        reject(new Error('reCAPTCHA timeout'))
      }, 10_000)

      resolveRef.current = (token: string) => {
        clearTimeout(timer)
        resolve(token)
      }
      rejectRef.current = (error: Error) => {
        clearTimeout(timer)
        reject(error)
      }

      webViewRef.current?.injectJavaScript(`
        (function() {
          if (typeof grecaptcha === 'undefined') {
            window.ReactNativeWebView.postMessage(JSON.stringify({error: 'grecaptcha not loaded'}));
            return;
          }
          grecaptcha.ready(function() {
            grecaptcha.execute('${SITE_KEY}', {action: '${action}'})
              .then(function(token) {
                window.ReactNativeWebView.postMessage(JSON.stringify({token: token}));
              })
              .catch(function(err) {
                window.ReactNativeWebView.postMessage(JSON.stringify({error: err.toString()}));
              });
          });
        })();
        true;
      `)
    })
  }

  const onWebViewMessage = (event: { nativeEvent: { data: string } }) => {
    try {
      const data = JSON.parse(event.nativeEvent.data) as { token?: string; error?: string }
      if (data.token) {
        resolveRef.current?.(data.token)
      } else if (data.error) {
        rejectRef.current?.(new Error(data.error))
      }
      resolveRef.current = null
      rejectRef.current = null
    } catch {
      // ignorar errores de parseo
    }
  }

  const recaptchaElement =
    Platform.OS !== 'web' && NativeWebView
      ? React.createElement(NativeWebView, {
          ref: webViewRef,
          source: { html: RECAPTCHA_HTML },
          onMessage: onWebViewMessage,
          style: { width: 0, height: 0, opacity: 0, position: 'absolute' },
          javaScriptEnabled: true,
          domStorageEnabled: true,
          originWhitelist: ['*'],
        })
      : null

  return { getToken, recaptchaElement }
}
